import { useEffect, useRef } from "react";
import dayjs from "dayjs";

/**
 * How long fetched schedule data counts as fresh. Past it the page asks again: the admin grid,
 * the pending list beside it and the guest's free slots all expire on this one clock.
 */
export const SCHEDULE_MAX_AGE_MS = 5 * 60_000;

// A runaway guard for `refetchDaysThrough`, far past any range a user pages out to.
const MAX_REFRESH_PAGES = 20;

/**
 * Calls `refresh` once the data loaded at `loadedAt` is `maxAgeMs` old: the hand-rolled
 * counterpart of a TanStack `staleTime` + `refetchInterval`, for the paging schedule hooks,
 * which keep their days in component state rather than in the query cache.
 *
 * A hidden tab is not refreshed — the request would be wasted, and background timers are
 * throttled anyway. It is refreshed the moment it is shown again, if it went stale meanwhile.
 *
 * `loadedAt` is the caller's clock. Stamp it when a (re)load *starts*, and the countdown starts
 * over from there whether or not that request succeeds — so a failed refresh is retried one
 * `maxAgeMs` later rather than never. `null` means nothing is loaded and nothing is scheduled.
 */
const useRefreshWhenStale = (refresh, loadedAt, maxAgeMs = SCHEDULE_MAX_AGE_MS) => {
  // The latest `refresh`, without re-arming the timer on every render: the callers recreate it.
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  });

  useEffect(() => {
    if (loadedAt == null) return undefined;

    const refreshIfShown = () => {
      if (document.visibilityState !== "visible") return false;
      refreshRef.current();
      return true;
    };

    const onVisibilityChange = () => {
      if (refreshIfShown()) document.removeEventListener("visibilitychange", onVisibilityChange);
    };

    const timer = setTimeout(() => {
      if (!refreshIfShown()) document.addEventListener("visibilitychange", onVisibilityChange);
    }, Math.max(loadedAt + maxAgeMs - Date.now(), 0));

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [loadedAt, maxAgeMs]);
};

/**
 * Fetches the days from today through `lastDate` again, page by page, the way the paging hooks
 * first loaded them: each page starts the day after the previous one ended. Days past
 * `lastDate` are trimmed off, so a refresh never lengthens what the user has paged out to.
 *
 * `fetchPage(startDate)` resolves to a page of days, or `null` when the request failed — and
 * then so does this, so the caller keeps what is on screen.
 *
 * Starting at today rather than at the first day on screen is deliberate: a page left open
 * past midnight would otherwise ask for yesterday, which the backend rejects.
 */
export const refetchDaysThrough = async (fetchPage, lastDate) => {
  const days = [];
  let startDate = dayjs();

  for (let page = 0; page < MAX_REFRESH_PAGES; page++) {
    const pageDays = await fetchPage(startDate);
    if (!pageDays) return null;

    days.push(...pageDays);

    const pageEnd = pageDays[pageDays.length - 1]?.date;
    if (!pageEnd || pageEnd >= lastDate) break;
    startDate = dayjs(pageEnd).add(1, "day");
  }

  return days.filter((day) => day.date <= lastDate);
};

export default useRefreshWhenStale;
